import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { Skeleton } from 'src/components/ui/skeleton';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import {
  BackArrowIcon,
  getKnowledgeBaseIcon,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseIcons/CurrentKnowledgeBaseIcons';
import {
  editEmptyFolder,
  getChildrenFiles,
  getRootFolder,
  removeEmptyFolders,
  setCheckedChildren,
  updateBranchInTree,
} from 'src/utils/fileUtils';
import { useLoader } from 'src/provider/LoaderProvider';
import {
  useUpdateKnowledgeBaseFileMutation,
  useUpdateKnowledgeBaseFilesMutation,
} from 'src/services/knowledgeBaseService';
import { CurrentKnowledgeBaseTableRow } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseTableList/CurrentKnowledgeBaseTableRow/CurrentKnowledgeBaseTableRow';
import { useKBContext } from 'src/provider/KnowledgeBaseProvider';
import { useAppSelector } from 'src/store';
import { FILE_STATE } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';

export const CurrentKnowledgeBaseTableList = ({
  onCheckedChange,
  isLoadingFiles,
  deleteContentMode,
  moveContentMode,
  handleItemsDelete,
  handleMoveItems,
  className,
}: {
  onCheckedChange: any;
  isLoadingFiles: any;
  deleteContentMode: any;
  moveContentMode: any;
  handleItemsDelete: any;
  handleMoveItems: any;
  className?: string;
}) => {
  const [isEmpty, setIsEmpty] = useState(false);
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);
  const { setIsEditPopupOpen, setSelectedItem, activeBranch, updateActiveBranch } = useKBContext();

  const [
    updateKnowledgeBaseFiles,
    { isLoading: isLoadingUpdateKnowledgeBaseFiles, isSuccess: isSuccessUpdateKnowledgeBaseFiles },
  ] = useUpdateKnowledgeBaseFilesMutation();

  const [
    updateKnowledgeBaseFile,
    { isLoading: isLoadingUpdateKnowledgeBaseFile, isSuccess: isSuccessUpdateKnowledgeBaseFile },
  ] = useUpdateKnowledgeBaseFileMutation();

  useEffect(() => {
    if (isSuccessUpdateKnowledgeBaseFiles) {
      notification.success('Updated Successfully.');
    }
  }, [isSuccessUpdateKnowledgeBaseFiles]);

  useEffect(() => {
    if (isSuccessUpdateKnowledgeBaseFile) {
      notification.success('Updated Successfully.');
    }
  }, [isSuccessUpdateKnowledgeBaseFile]);

  useEffect(() => {
    if (isLoadingUpdateKnowledgeBaseFiles || isLoadingUpdateKnowledgeBaseFile) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingUpdateKnowledgeBaseFiles, isLoadingUpdateKnowledgeBaseFile]);

  useEffect(() => {
    if (activeBranch) {
      setIsEmpty(!activeBranch.children.some((child) => child.visible));
    }
  }, [activeBranch]);

  const handleRowClick = (item) => {
    const is = {
      file: item.type === 'file',
      folder: item.type === 'folder',
      md: item.name.endsWith('.md'),
      txt: item.name.endsWith('.txt'),
      faq: item.type === 'FAQ',
      integration: item.type === 'integration' && !item.collector_id,
      zendeskTickets: Boolean(item?.data?.type === 'zendesk_tickets'),
      freshdeskTickets: Boolean(item?.data?.type === 'freshdesk_tickets'),
      website: item.type === 'website' && !item.collector_id,
      websiteChild: item.type === 'website' && Boolean(item.collector_id),
      selection: Boolean(window.getSelection().toString().length),
    };

    if (is.selection) {
      return;
    }

    setSelectedItem(item);

    if (!is.folder && (is.md || is.faq || is.txt || is.websiteChild)) {
      if (![FILE_STATE.DELETING, FILE_STATE.UPLOADING].includes(item.state)) {
        setIsEditPopupOpen(true);
        return;
      }
    }

    if (is.zendeskTickets || is.freshdeskTickets) {
      return;
    }

    if (is.folder || is.integration || is.website) {
      updateActiveBranch(item);
    }
  };

  const handleSingleItemEdit = async (name, item) => {
    let files = [item];

    if (item.type === 'folder') {
      files = getChildrenFiles(item);

      if (!files.length) {
        const newActiveBranch = {
          ...activeBranch,
          children: activeBranch.children.filter((folder) => folder.path !== item.path),
        };

        editEmptyFolder(name, item);
        updateActiveBranch(newActiveBranch);
        notification.success('Updated Successfully.');
        return;
      }
    }

    const request = files.map((file) => {
      if (item.type === 'file' || item.type === 'FAQ') {
        return {
          id: file.id,
          name: item.type === 'FAQ' ? `${name}.faq` : name,
          path: file.parent.path,
        };
      }
      if (item.type === 'folder') {
        const newPath = item.path.replace(`/${item.name}`, `/${name}`);

        return {
          id: file.id,
          name: file.name,
          path: file.path.replace(`${item.path}`, newPath).replace(`/${file.name}`, ''),
        };
      }
    });

    if (request.length === 1) {
      await updateKnowledgeBaseFile({
        knowledgeBaseId: id,
        fileId: request[0].id,
        body: {
          name: request[0].name,
          path: request[0].path,
        },
      });
    }

    if (request.length > 1) {
      await updateKnowledgeBaseFiles({
        id: id,
        body: request,
      });
    }
  };

  const handleSingleItemMove = async (moveToFolder, item) => {
    item.checked = true;
    if (item.type === 'folder') {
      setCheckedChildren(item);
    }

    await handleMoveItems(moveToFolder);
  };

  const handleSingleItemDelete = async (item) => {
    if (deleteContentMode) {
      await handleItemsDelete();

      return;
    }

    const tree = getRootFolder(item);

    if (item.type === 'folder') {
      const files = getChildrenFiles(item);

      if (files.length) {
        await handleItemsDelete(files);
      } else {
        const newActiveBranch = { ...activeBranch };

        newActiveBranch.children = newActiveBranch.children.filter((child) => child !== item);
        removeEmptyFolders([item]);
        updateBranchInTree(tree, activeBranch, newActiveBranch);
        updateActiveBranch(newActiveBranch);
      }
    } else {
      await handleItemsDelete([item]);
    }
  };

  return (
    <Table
      wrapperClassName="max-h-[71vh] py-0"
      className={'relative !border-separate !border-spacing-y-[1px]'}
    >
      <TableHeader className={'bg-white z-10'}>
        <TableRow>
          <TableHead className="text-center text-grey_text"></TableHead>
          <TableHead className="text-start text-grey_text">Name</TableHead>
          <TableHead className="text-center text-grey_text">Type</TableHead>
          <TableHead className="text-center text-grey_text">Updated</TableHead>
          {!isTrialPlan && <TableHead className="text-center text-grey_text">Auto-sync</TableHead>}
          <TableHead className="text-grey_text"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody className="">
        {Boolean(activeBranch?.parent) && (
          <TableRow
            className={
              'transition cursor-pointer hover:shadow-[0px_1px_2px_0px_rgba(0,_0,_0,_0.25)] bg-[#F4F8F7] hover:bg-[#E2EAE8]'
            }
            onClick={() => handleRowClick(activeBranch?.parent)}
          >
            <TableCell
              padding={'pl-5 pr-0'}
              className="table-fixedt text-center min-w-[50px] max-w-[100px] border-[#E3E6E8] border-l !rounded-tl-none"
            >
              <div className="whitespace-nowrap hyphens-auto truncate flex items-center justify-center gap-2.5">
                <div>{getKnowledgeBaseIcon(activeBranch?.parent)}</div>
                <div className={'min-w-6'}></div>
              </div>
            </TableCell>
            <TableCell
              colSpan={5}
              padding={'p-5'}
              className="table-fixedt text-start max-w-80 border-[#E3E6E8] !rounded-tr-none"
            >
              <div className="whitespace-nowrap hyphens-auto truncate flex items-center justify-start">
                {<BackArrowIcon />}
              </div>
            </TableCell>
          </TableRow>
        )}
        {isEmpty && (
          <TableRow>
            <TableCell colSpan={6} className={'!rounded-t-none'}>
              <LoaderWrapper
                loader={<Skeleton className="w-full h-6" />}
                isLoading={isLoadingFiles}
              >
                <p className="opacity-70 font-semibold text-base text-ebony text-center">
                  {activeBranch.root
                    ? 'No data sources yet'
                    : `${activeBranch.type ? activeBranch.type[0].toUpperCase() + activeBranch.type.slice(1) : 'Folder'} is empty`}
                </p>
              </LoaderWrapper>
            </TableCell>
          </TableRow>
        )}
        {!isEmpty &&
          activeBranch.children.map((item, index) => {
            return (
              <CurrentKnowledgeBaseTableRow
                key={`item-name-${item.name}-${item.id}-${index}`}
                item={item}
                handleRowClick={handleRowClick}
                handleSingleItemEdit={handleSingleItemEdit}
                handleSingleItemMove={handleSingleItemMove}
                handleSingleItemDelete={handleSingleItemDelete}
                onCheckedChange={onCheckedChange}
                deleteContentMode={deleteContentMode}
                moveContentMode={moveContentMode}
              />
            );
          })}
      </TableBody>
    </Table>
  );
};
